import { useState, useEffect } from "react";
import { GoSettings, GoX } from "react-icons/go";
import Logo from "../assets/logo.png";
import * as config from "../config";

const useLocations = (province, municipality) => {
  const [provinces, setProvinces] = useState([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);
  const [municipalities, setMunicipalities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [isLoadingMunicipalities, setIsLoadingMunicipalities] = useState(false);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoadingProvinces(true);
      const response = await fetch(`${config.SERVER_URL}/provinces`);
      const data = await response.json();
      setProvinces(data);
      setIsLoadingProvinces(false);
    })();
  }, []);

  useEffect(() => {
    if (province) {
      (async () => {
        setIsLoadingMunicipalities(true);
        setLocalities([]);
        const response = await fetch(`${config.SERVER_URL}/provinces/${province}/municipalities`);
        const data = await response.json();
        setMunicipalities(data);
        setIsLoadingMunicipalities(false);
      })();
    }
  }, [province]);

  useEffect(() => {
    if (municipality) {
      (async () => {
        setIsLoadingLocalities(true);
        const response = await fetch(`${config.SERVER_URL}/provinces/${province}/municipalities/${municipality}/localities`);
        const data = await response.json();
        setLocalities(data);
        setIsLoadingLocalities(false);
      })();
    }
  }, [municipality]);

  return {
    provinces,
    municipalities,
    localities,
    isLoadingProvinces,
    isLoadingMunicipalities,
    isLoadingLocalities,
  }
}

export const Aside = ({
  searchParams = {},
  onChangeSearchParam = (name, value) => { },
  resetFilters = () => { }
}) => {
  const {
    provinces,
    municipalities,
    localities,
    isLoadingProvinces,
    isLoadingMunicipalities,
    isLoadingLocalities
  } = useLocations(searchParams.province, searchParams.municipality);
  const [asideOpen, setAsideOpen] = useState(false);

  return <>
    <button
      className="md:hidden z-[9999] fixed right-4 top-4 bg-white rounded-lg p-1 shadow"
      onClick={() => setAsideOpen(true)}
    >
      <GoSettings className="w-10 h-10" />
    </button>

    <aside className={`fixed left-0 top-0 h-screen w-screen md:w-96 p-5 md:border-r z-[9999] bg-white md:translate-x-0 ${asideOpen ? "translate-x-0" : "-translate-x-full"} transition-transform`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <img
            src={Logo}
            alt="Logo comparador de gasolineras"
            className="w-12 h-12 mr-2"
          />
          <span className="font-bold text-2xl after:block after:w-12 after:h-1.5 after:bg-primary after:absolute">Gasolineras <span className="text-black">es</span>pañolas</span>
        </div>
        <button
          onClick={() => setAsideOpen(false)}
          className="md:hidden"
        >
          <GoX className="w-7 h-7" />
        </button>
      </div>

      <div className="mt-10">
        <h4 className="font-semibold text-2xl text-gray-800 border-l-primary border-l-4 pl-2 mb-2">Filtros</h4>

        <div className={`mb-4 ${Object.entries(searchParams).filter(([_, value]) => Boolean(value)).some(([key]) => !key.toString().startsWith("map")) ? "block" : "hidden"}`}>
          <button
            onClick={resetFilters}
            className="text-red-400 font-bold"
          >
            Borrar filtros
          </button>
        </div>

        <div className="mb-4">
          <label htmlFor="province" className="font-bold text-gray-600 block text-lg mb-1">Provincia</label>
          <select
            id="province"
            name="province"
            className="block w-full p-2 rounded-md border-2 bg-white focus:border-primary outline-none disabled:cursor-not-allowed"
            onChange={e => onChangeSearchParam(e.target.name, e.target.value)}
            value={searchParams.province || ""}
            disabled={isLoadingProvinces}
          >
            <option value="">Seleccione</option>
            {provinces.map((province, index) => <option
              key={index}
              value={province}
            >
              {province}
            </option>)}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="municipality" className="font-bold text-gray-600 block text-lg mb-1">Municipio</label>
          <select
            id="municipality"
            name="municipality"
            className="block w-full p-2 rounded-md border-2 bg-white focus:border-primary outline-none disabled:cursor-not-allowed"
            onChange={e => onChangeSearchParam(e.target.name, e.target.value)}
            value={searchParams.municipality || ""}
            disabled={!searchParams.province || isLoadingMunicipalities}
          >
            <option value="">Seleccione</option>
            {municipalities.map((municipality, index) => <option
              key={index}
              value={municipality}
            >
              {municipality}
            </option>)}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="locality" className="font-bold text-gray-600 block text-lg mb-1">Localidad</label>
          <select
            id="locality"
            name="locality"
            className="block w-full p-2 rounded-md border-2 bg-white focus:border-primary outline-none disabled:cursor-not-allowed"
            onChange={e => onChangeSearchParam(e.target.name, e.target.value)}
            value={searchParams.locality || ""}
            disabled={!searchParams.municipality || isLoadingLocalities}
          >
            <option value="">Seleccione</option>
            {localities.map((locality, index) => <option
              key={index}
              value={locality}
            >
              {locality}
            </option>)}
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="typeOfFuel" className="font-bold text-gray-600 block text-lg mb-1">Tipo de combustible</label>
          <select
            id="typeOfFuel"
            name="typeOfFuel"
            className="block w-full p-2 rounded-md border-2 bg-white focus:border-primary outline-none"
            onChange={e => onChangeSearchParam(e.target.name, e.target.value)}
            value={searchParams.typeOfFuel}
          >
            <option value="">Seleccione</option>
            <option value="g_95_e5">Gasolina 95 E5</option>
            <option value="g_95_e10">Gasolina 95 E10</option>
            <option value="g_95_e5_premium">Gasolina 95 E5 Premium</option>
            <option value="g_98_e5">Gasolina 98 E5</option>
            <option value="g_98_e10">Gasolina 98 E10</option>
            <option value="d_a">Diésel A (habitual)</option>
            <option value="d_premium">Diésel Premium</option>
            <option value="d_b">Diésel B</option>
          </select>
        </div>

        <div className="mb-4">
          <label htmlFor="typeOfSell" className="font-bold text-gray-600 block text-lg mb-1">Tipo de venta</label>
          <select
            id="typeOfSell"
            name="typeOfSell"
            className="block w-full p-2 rounded-md border-2 bg-white focus:border-primary outline-none"
            onChange={e => onChangeSearchParam(e.target.name, e.target.value)}
            value={searchParams.typeOfSell}
          >
            <option value="">Seleccione</option>
            <option value="P">Venta al público</option>
            <option value="R">Venta a cooperativistas</option>
          </select>
        </div>
      </div>
      <p className="mt-10">
        <span>[+]</span> Información actualizada y proporcionada por <a
          href="https://geoportalgasolineras.es/"
          className="text-primary font-bold"
          target="_blank"
          rel="noreferrer"
        >
          geoportalgasolineras.es
        </a>.
      </p>
    </aside>
  </>
}